import React, { useState, useEffect } from 'react';
import '../Properties/property.scss';
import PropTypes from 'prop-types';
import DescriptionIcon from '@mui/icons-material/Description';
import CloseIcon from '@mui/icons-material/Close';
import PropertyLayout from './layout';
import { uploadFiles, addPropertyDetail, fetchFileById } from '../../Api/request/propertyDetail';
import UseAxios from '../../Api/http/http';
import useApiState from '../../Hooks/useApiState';
import LoaderOverlay from '../../Components/Loader/LoaderOverlay';
import { propertyAPI } from '../../services/api';

const ACCEPTED_FILE_TYPES = [
  'application/pdf',
  'application/msword',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'image/jpeg',
  'image/png',
  'image/gif',
  'image/webp',
  'image/jpg',
];

function PropertyTaxForm({ prevStep, onNext, onSave, onBackToDashboard, initialValues = {}, step = 6, totalSteps = 10, systemId, isEdit = false, isView = false }) {
  const {
    isSubmitting,
    submitError,
    clearErrors,
    submitData,
  } = useApiState({
    showToastOnError: true,
    defaultErrorMessage: 'Failed to save tax information',
  });

  const initialState = {
    taxPaidUpTo: null,
    receipts: [],
  };
  const [form, setForm] = useState(() => {
    // Don't include receipts from initialValues yet - they'll be loaded properly in useEffect
    // eslint-disable-next-line no-unused-vars
    const { receipts, property_documents, ...restInitialValues } = initialValues;
    return { ...initialState, ...restInitialValues };
  });
  const [fileError, setFileError] = useState('');
  const [modalImage, setModalImage] = useState(null);
  const [isLoadingFiles, setIsLoadingFiles] = useState(false);

  // Function to fetch existing tax receipt files for edit mode
  const fetchExistingTaxReceipts = async (taxReceiptFiles) => {
    if (!taxReceiptFiles || !Array.isArray(taxReceiptFiles) || taxReceiptFiles.length === 0) return [];

    console.log(`Fetching ${taxReceiptFiles.length} tax receipt files...`);
    const fetchedFiles = [];
    for (const fileInfo of taxReceiptFiles) {
      try {
        const response = await UseAxios(fetchFileById({ fileId: fileInfo.id }));
        if (response && response.data) {
          let blob;
          if (response.data instanceof Blob) {
            blob = response.data;
          } else if (response.data instanceof ArrayBuffer) {
            blob = new Blob([response.data], { type: fileInfo.type || 'application/octet-stream' });
          } else {
            console.error('Invalid response data type for tax receipt file:', fileInfo.name);
            continue; // Skip this file if data is invalid
          }
          if (blob && blob.size > 0) { // Ensure blob has content
            const url = URL.createObjectURL(blob);
            const file = new File([blob], fileInfo.name, { type: fileInfo.type });
            fetchedFiles.push({
              url,
              name: fileInfo.name,
              file,
              type: fileInfo.type,
              id: fileInfo.id, // Keep the original file ID for reference
              uploadUrl: fileInfo.uploadUrl, // Preserve upload URL
              gcsUrl: fileInfo.gcsUrl, // Preserve GCS URL
            });
          } else {
            console.error('Empty or invalid blob for tax receipt file:', fileInfo.name);
          }
        } else {
          console.error('No data in response for tax receipt file:', fileInfo.name);
        }
      } catch (error) {
        console.error('Error fetching tax receipt file:', error);
      }
    }

    console.log(`Successfully fetched ${fetchedFiles.length} tax receipt files`);
    return fetchedFiles;
  };

  // Load existing tax receipt files when in edit mode
  useEffect(() => {
    const loadExistingFiles = async () => {
      if ((isEdit || isView) && initialValues) {
        setIsLoadingFiles(true);
        try {
          let existingReceipts = [];

          // Fetch existing tax receipts
          if (initialValues.property_documents?.tax_receipt) {
            existingReceipts = await fetchExistingTaxReceipts(initialValues.property_documents.tax_receipt);
          }

          setForm(prev => {
            // Check if we already have user-uploaded files that shouldn't be overridden
            const hasUserUploads = prev.receipts && prev.receipts.length > 0;

            if (hasUserUploads) {
              // Merge existing files with user uploads
              const mergedReceipts = [...existingReceipts, ...prev.receipts];
              return {
                ...prev,
                receipts: mergedReceipts,
              };
            }

            // Set the properly fetched receipts
            return {
              ...prev,
              receipts: existingReceipts,
            };
          });
        } catch (error) {
          console.error('Error loading existing tax receipts:', error);
          setFileError('Failed to load existing tax receipts');
        } finally {
          setIsLoadingFiles(false);
        }
      } else if (initialValues) {
        setForm(prev => {
          // For non-edit mode, just merge initialValues without fetching files
          return { ...prev, ...initialValues };
        });
      }
    };

    loadExistingFiles();
  }, [initialValues, isEdit, isView]);

  // Cleanup blob URLs when component unmounts
  useEffect(() => {
    return () => {
      form.receipts.forEach(receipt => {
        if (receipt.url && receipt.url.startsWith('blob:')) {
          URL.revokeObjectURL(receipt.url);
        }
      });
    };
  }, [form.receipts]);

  // Sync initialValues if they change (for navigation between steps)
  useEffect(() => {
    if (initialValues && !isEdit && !isView) {
      setForm((prev) => {
        // Check if initialValues has receipt data that should be preserved
        const hasInitialReceipts = initialValues.receipts &&
          Array.isArray(initialValues.receipts) &&
          initialValues.receipts.length > 0;

        // Check if current form has user uploads
        const hasUserUploads = prev.receipts &&
          prev.receipts.length > 0 &&
          prev.receipts.some(file => !file.isExisting);

        // If we have initial receipts or user uploads, merge them properly
        if (hasInitialReceipts || hasUserUploads) {
          // If user has uploads, preserve them
          if (hasUserUploads) {
            return { ...prev, ...initialValues };
          }
          return { ...prev, ...initialValues };
        }

        return prev;
      });
    }
  }, [initialValues, isEdit, isView]);

  const handleChange = (event) => {
    clearErrors();
    const { name, value } = event.target;
    setForm((prev) => ({ ...prev, [name]: value ? value : null }));
  };

  const handleFiles = (newFiles) => {
    setFileError('');
    clearErrors();
    const fileArray = Array.from(newFiles);
    if (fileArray.length + form.receipts.length > 3) {
      setFileError('Maximum 3 files allowed.');
      return;
    }
    const validFiles = [];
    for (let file of fileArray) {
      if (file.size > 10 * 1024 * 1024) {
        setFileError(`${file.name} exceeds 10MB.`);
        continue;
      }
      if (!ACCEPTED_FILE_TYPES.includes(file.type)) {
        setFileError(`File type not supported: ${file.name}`);
        continue;
      }
      validFiles.push({
        url: file.type.startsWith('image/') ? URL.createObjectURL(file) : null,
        name: file.name,
        type: file.type,
        file,
      });
    }
    const newReceipts = [...form.receipts, ...validFiles].slice(0, 3);
    setForm((prev) => ({
      ...prev,
      receipts: newReceipts,
      taxReceiptExist: newReceipts.length > 0,
    }));
  };

  const handleFileSelect = (event) => {
    handleFiles(event.target.files);
    event.target.value = null;
  };

  const removeFile = (index) => {
    const fileToDelete = form.receipts?.[index];
    const fileIdToDelete =
      fileToDelete?.id ||
      fileToDelete?.fileId ||
      fileToDelete?.file_id ||
      fileToDelete?.driveFileId ||
      null;

    if (fileIdToDelete) {
      // Best-effort delete call; keep existing UI behavior unchanged on failure.
      void propertyAPI.deletePropertyImage(String(fileIdToDelete)).catch((error) => {
        console.warn('Failed to delete tax receipt from drive', {
          fileId: String(fileIdToDelete),
          error,
        });
      });
    }

    setForm((prev) => {
      const file = prev.receipts[index];
      // Only revoke URLs for user-uploaded files, not existing ones
      if (file && file.url && !file.isExisting) {
        URL.revokeObjectURL(file.url);
      }
      return { ...prev, receipts: prev.receipts.filter((file, idx) => idx !== index) };
    });
    if (modalImage && form.receipts[index] && form.receipts[index].url === modalImage) {
      setModalImage(null);
    }
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    clearErrors();

    try {
      await submitData(async () => {
        const { receipts } = form;
        let allReceiptMetaData = [];

        // Separate existing files (with IDs) from new files (without IDs)
        const existingFiles = receipts.filter(rec => rec.id); // Files with IDs are existing
        const newFiles = receipts.filter(rec => !rec.id); // Files without IDs are new

        // Upload only new files
        if (newFiles.length > 0) {
          const formData = new FormData();
          newFiles.forEach((rec) => {
            if (rec.file instanceof File) {
              formData.append('images', rec.file, rec.name);
            }
          });
          formData.append('type', 'Files');
          formData.append('subType', 'Tax Receipt');
          formData.append('systemId', systemId);

          const response = await UseAxios(uploadFiles(formData));
          if (response?.data?.receiptData) {
            allReceiptMetaData = [...allReceiptMetaData, ...response.data.receiptData];
          }
        }

        // Add existing files metadata (preserve original IDs and structure)
        const existingFileMetaData = existingFiles.map(file => ({
          id: file.id,
          name: file.name,
          type: file.type,
          uploadUrl: file.uploadUrl,
          gcsUrl: file.gcsUrl,
        }));
        allReceiptMetaData = [...existingFileMetaData, ...allReceiptMetaData];
        // Save combined metadata (existing + new)
        if (allReceiptMetaData.length > 0) {
          setForm((prev) => ({
            ...prev,
            receiptMetaData: allReceiptMetaData,
          }));

          const payload = {
            property_id: systemId,
            documentType: 'Tax Receipt',
            documentSave: true,
            documentMetaData: allReceiptMetaData,
          };
          await UseAxios(addPropertyDetail(payload));
        }

        return form;
      }, {
        successMessage: 'Tax information saved successfully',
      });

      onNext(form);
    } catch (error) {
      console.error('Failed to save tax information:', error);
    }
  };
  // Helper to save form data before navigating
  const handlePrev = () => {
    onSave(form); // Save current form data including uploaded files
    prevStep(); // Go to previous step
  };

  const handleNext = () => {
    onSave(form); // Save current form data
    onNext(form); // Pass form data to next step
  };

  return (
    <PropertyLayout step={step} totalSteps={totalSteps} systemId={systemId} onBackToDashboard={onBackToDashboard}>
      <LoaderOverlay isLoading={isSubmitting || isLoadingFiles} />
      <div className='card'>
        <h3 className='card-title'>
          <DescriptionIcon style={{ verticalAlign: 'middle', marginRight: 8 }} />
          Tax Information
          {isEdit && (
            <span style={{ fontSize: '0.8em', color: '#666', marginLeft: '10px' }}>
              (Edit Mode)
            </span>
          )}
          {isView && (
            <span style={{ fontSize: '0.8em', color: '#666', marginLeft: '10px' }}>
              (View Mode)
            </span>
          )}
        </h3>

        {/* Display loading for existing files */}
        {isLoadingFiles && (
          <div className="loading-message" style={{
            padding: '10px',
            marginBottom: '15px',
            backgroundColor: '#e3f2fd',
            color: '#1976d2',
            borderRadius: '4px',
            border: '1px solid #bbdefb',
            textAlign: 'center',
          }}>
            Loading existing tax receipts...
          </div>
        )}

        {/* Display API errors */}
        {submitError && (
          <div className="error-message" style={{
            padding: '10px',
            marginBottom: '15px',
            backgroundColor: '#ffebee',
            color: '#c62828',
            borderRadius: '4px',
            border: '1px solid #ffcdd2',
          }}>
            {submitError}
          </div>
        )}

        <form onSubmit={handleSubmit}>
          <div className='form-row-three'>
            <div className='form-group'>
              <label htmlFor='taxDate'>Tax Paid Up To</label>
              <input
                type='date'
                id='taxDate'
                name='taxPaidUpTo'
                value={form.taxPaidUpTo || ''}
                onChange={handleChange}
                placeholder='mm/dd/yyyy'
                disabled={isSubmitting || isView}
                readOnly={isView}
              />
            </div>
          </div>
          {/* File upload field - custom button and label */}
          <div className='form-group'>
            <label style={{ marginBottom: 8, display: 'block' }}>Tax Payment Receipt</label>
            <div className='choose-files'>
              <input
                type='file'
                id='taxReceipt'
                accept={ACCEPTED_FILE_TYPES.join(',')}
                multiple
                onChange={handleFileSelect}
                style={{ display: 'none' }}
              />
              <button
                type='button'
                className='btn primary choose-files-btn'
                onClick={() => document.getElementById('taxReceipt').click()}
                disabled={isSubmitting || isView}
                style={{ display: isView ? 'none' : 'block' }}
              >
                Choose Files
              </button>
              <div className='file-names-area' style={{ minHeight: 32, flex: 1, display: 'flex', alignItems: 'center' }}>
                {form.receipts.length === 0 ? (
                  <span style={{ color: '#888', fontSize: 14, marginLeft: 8 }}>No file chosen</span>
                ) : null}
              </div>
            </div>
            {/* Selected files row-wise, aligned with button */}
            {form.receipts.length > 0 && (
              <div className='preview-container' style={{ display: 'flex', flexDirection: 'column', gap: 8, marginTop: 8, marginLeft: 0, alignItems: 'flex-start' }}>
                {form.receipts.map((file, idx) => (
                  <div key={idx} className='preview-wrapper' style={{ display: 'flex', alignItems: 'center', gap: 8, background: '#f3f4f6', borderRadius: 6, padding: '4px 12px', width: 'fit-content' }}>
                    <span
                      className='preview-label'
                      style={{ cursor: file.url ? 'pointer' : 'default', color: file.url ? '#1976d2' : '#333', textDecoration: file.url ? 'underline' : 'none', fontWeight: 500, fontSize: 15, fontFamily: 'Mukta, Arial, sans-serif' }}
                      onClick={() => file.url && setModalImage(file.url)}
                    >
                      {file.name}
                    </span>
                    {!isView && (
                      <span
                        className='cancel-icon'
                        onClick={(event) => {
                          event.stopPropagation();
                          removeFile(idx);
                        }}
                        style={{ marginLeft: 4, cursor: 'pointer' }}
                      >
                        <CloseIcon fontSize='small' />
                      </span>
                    )}
                  </div>
                ))}
              </div>
            )}
            {fileError && <div className='field-note-error' style={{ marginTop: 8 }}>{fileError}</div>}
          </div>
          {/* Modal for image preview */}
          {modalImage && (
            <div className='modal-overlay' style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.6)', zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <div className='modal-content' style={{ position: 'relative', background: '#fff', padding: 24, borderRadius: 8, boxShadow: '0 2px 8px rgba(0,0,0,0.2)' }}>
                <span
                  className='cancel-icon'
                  onClick={() => setModalImage(null)}
                  style={{ position: 'absolute', top: 8, right: 8, cursor: 'pointer', zIndex: 2 }}
                >
                  <CloseIcon fontSize='medium' />
                </span>
                <img src={modalImage} alt='Preview' style={{ maxWidth: '80vw', maxHeight: '80vh', borderRadius: 8 }} />
              </div>
            </div>
          )}
          {/* Actions */}
          <div className='form-actions'>
            <button type='button' className='btn secondary' onClick={handlePrev} disabled={isSubmitting}>
              Previous
            </button>
            {isView ? (
              <button
                type='button'
                className='btn primary'
                onClick={handleNext}
              >
                Next
              </button>
            ) : (
              <button type='submit' className='btn primary' disabled={isSubmitting}>
                {isSubmitting ? 'Saving...' : 'Next'}
              </button>
            )}
          </div>
        </form>
      </div>
    </PropertyLayout>
  );
}

PropertyTaxForm.propTypes = {
  prevStep: PropTypes.func.isRequired,
  onNext: PropTypes.func.isRequired,
  onSave: PropTypes.func.isRequired,
  onBackToDashboard: PropTypes.func,
  initialValues: PropTypes.object,
  step: PropTypes.number,
  totalSteps: PropTypes.number,
  systemId: PropTypes.string,
  isEdit: PropTypes.bool,
  isView: PropTypes.bool,
};

export default PropertyTaxForm;
